"use client";

import "./page.scss";
import { useEffect, useState } from "react";
import { useUnit } from "effector-react";
import { FEATURES } from "@features/features-list";
import { AmplitudeFlagsModule, type LandingV4Variant } from "@features/amplitude-flags/model";
import dynamic from "next/dynamic";
import { AnalyticsService } from "@features/shared/analytics";

// Each arm in its own chunk: a visitor downloads and parses only the landing
// they were bucketed into. The page renders null until the flag resolves
// anyway, so deferring the component costs no extra paint.
// The previous base landing (LandingTest, winner of the old `landing_variant`
// A/B) stays in the tree but is no longer wired here.
const LandingV3 = dynamic(
    () => import("@components/organisms/landing-ui/landingV3/landingV3").then((m) => m.LandingV3),
    { ssr: false, loading: () => null }
);
const LandingV4 = dynamic(
    () => import("@components/organisms/landing-ui/landingV4/landingV4").then((m) => m.LandingV4),
    { ssr: false, loading: () => null }
);

const VARIANT_OVERRIDE_PARAM = "variant";

export default function Page() {
    const amplitudeFlagsContract = FEATURES.getByType<AmplitudeFlagsModule>(AmplitudeFlagsModule).getContract();
    const [landingV4Variant, flagsLoaded] = useUnit([
        amplitudeFlagsContract.stores.$landingV4Variant,
        amplitudeFlagsContract.stores.$flagsLoaded
    ]);

    // Dev-only override: append ?variant=test (or ?variant=control) to force an
    // arm locally without relying on the flags fetch succeeding.
    const [overrideVariant, setOverrideVariant] = useState<LandingV4Variant | null>(null);

    useEffect(() => {
        const param = new URLSearchParams(window.location.search).get(VARIANT_OVERRIDE_PARAM);
        if (param === "test" || param === "control") {
            setOverrideVariant(param);
        }
    }, []);

    const resolvedVariant: LandingV4Variant | null = overrideVariant ?? (flagsLoaded ? landingV4Variant : null);

    // Tag analytics so control vs test events can be told apart: a user property
    // for funnel-wide segmentation (the conversion event fires off-site under the
    // stitched identity) and a per-event context prop for event-level breakdowns.
    useEffect(() => {
        if (!resolvedVariant) return;
        AnalyticsService.setContextProps({ landing_v4_variant: resolvedVariant });
        AnalyticsService.setUserProps({ landing_v4_variant: resolvedVariant });
    }, [resolvedVariant]);

    if (!resolvedVariant) return null;

    // `landing_v4_variant`: "control" = LandingV3 (the shipped winner of the
    // retired landing_v3_variant A/B), "test" = LandingV4 from the v20 prototype.
    return resolvedVariant === "test" ? <LandingV4 /> : <LandingV3 />;
}
